import { reportBunkered, reportRob } from './fuel.js';
import type { LoadCondition, VoyageStatus } from './lists.js';
import type { VoyageReportKind } from './schemas.js';

/**
 * Sefer hesapları (ADR 0022 §4; müşterinin "Hesaplama Kuralları", `sefer-raporlama.md`). Saf
 * fonksiyonlar; süreler UTC ile hesaplanır, gemi saati farkının değişmesi süreyi bozmaz. Sonuçlar
 * kayan nokta artıklarını atmak için 3 haneye yuvarlanır.
 */

const round = (value: number, digits = 3) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};
const hoursBetween = (from: string, to: string) => (Date.parse(to) - Date.parse(from)) / 3_600_000;

/** Yakıt ve yağ kapanışı: satır okuyucularıyla bir yerde (ADR 0090 §1). */
export { closingRob } from './fuel.js';

/** Tatlı su: Closing = ROB − Consumption + Produced + Bunkered. */
export function freshWaterClosing(row: {
  rob: number;
  consumption: number;
  produced: number;
  bunkered: number;
}): number {
  return round(row.rob - row.consumption + row.produced + row.bunkered);
}

/** Önceki rapordan bu yana seyir süresi (saat); sıfır ya da ters süre `null`. */
export function steamingHours(previousAt: string, currentAt: string): number | null {
  const hours = hoursBetween(previousAt, currentAt);
  return Number.isFinite(hours) && hours > 0 ? round(hours) : null;
}

/**
 * Ortalama hız = mesafe / süre. Ekrandaki örnek (48.6 nm, 3 saat, 12.8 kn) bu formülle tutmuyor;
 * standart formül uygulanır (karar, ADR 0024 #14).
 */
export function averageSpeed(distanceNm: number, hours: number | null): number | null {
  return hours && hours > 0 ? round(distanceNm / hours) : null;
}

/** Teorik hız (kn) = pitch (m) × RPM × 60 / 1852 (karar, ADR 0024 #14). */
export function theoreticalSpeed(pitchM: number, rpm: number): number {
  return round((pitchM * rpm * 60) / 1852, 2);
}

/** Kayma % = (teorik hız − su üstü hız) / teorik hız × 100. */
export function slipPercent(theoreticalKn: number, stwKn: number): number | null {
  return theoreticalKn > 0 ? round(((theoreticalKn - stwKn) / theoreticalKn) * 100, 2) : null;
}

/**
 * Motor (pervane) mesafesi (nm) = pitch (m) × RPM × 60 × saat / 1852: standart slip formülünün
 * mesafeyle yazılışı (ajan kararı, ADR 0057 §4). `theoreticalSpeed` × saat ile aynıdır.
 */
export function engineDistanceNm(pitchM: number, rpm: number, hours: number): number {
  return round((pitchM * rpm * 60 * hours) / 1852, 2);
}

/**
 * Slip % = (motor mesafesi − gözlenen mesafe) / motor mesafesi × 100 (ADR 0057 §4). Gözlenen
 * mesafe su üstü (parakete) olduğunda `slipPercent` ile aynı ölçüdür; negatifse akıntı ya da
 * rüzgâr yardım etmiştir.
 */
export function distanceSlipPercent(engineNm: number, observedNm: number): number | null {
  return engineNm > 0 ? round(((engineNm - observedNm) / engineNm) * 100, 2) : null;
}

export interface VoyageStart {
  voyageId: string;
  /** Departure raporundaki pilot ayrılışı; yoksa sefer başlamamıştır. */
  start: string | null;
  cancelled?: boolean;
}

export interface VoyagePeriod {
  voyageId: string;
  start: string | null;
  end: string | null;
  status: VoyageStatus;
}

/**
 * Seferin başlangıcı, bitişi ve durumu (kural 1–3). Durum saklanmaz, raporlardan türer: başlangıç
 * Departure'daki pilot ayrılışıdır; bir sonraki seferin başlangıcı (yeni limandan kalkışta pilot
 * ayrılışı) öncekini bitirir. İptal edilen sefer başka seferi bitirmez.
 */
export function voyagePeriods(voyages: readonly VoyageStart[]): VoyagePeriod[] {
  const started = voyages
    .filter((v): v is VoyageStart & { start: string } => v.start !== null && !v.cancelled)
    .sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
  const periods: VoyagePeriod[] = started.map((voyage, index) => {
    const end = started[index + 1]?.start ?? null;
    return {
      voyageId: voyage.voyageId,
      start: voyage.start,
      end,
      status: end ? 'completed' : 'active',
    };
  });
  for (const voyage of voyages) {
    if (voyage.start !== null && !voyage.cancelled) continue;
    periods.push({
      voyageId: voyage.voyageId,
      start: voyage.start,
      end: null,
      status: voyage.cancelled ? 'cancelled' : 'planned',
    });
  }
  return periods;
}

/** Gönderilmiş rapor; performans yalnız gönderilmiş raporlardan hesaplanır. */
export interface PerformanceReport {
  kind: VoyageReportKind;
  reportedAt: string;
  data: Readonly<Record<string, unknown>>;
}

type Segment = 'strait' | 'canal' | 'anchorage' | 'drift' | 'waiting';
const OPENS: Partial<Record<VoyageReportKind, Segment>> = {
  strait_entry: 'strait',
  canal_entry: 'canal',
  anchorage_start: 'anchorage',
  drift_start: 'drift',
  waiting_start: 'waiting',
};
const CLOSES: Partial<Record<VoyageReportKind, Segment>> = {
  strait_exit: 'strait',
  canal_exit: 'canal',
  anchorage_end: 'anchorage',
  drift_end: 'drift',
  waiting_end: 'waiting',
};

/** İki ardışık rapor arası: süre, mesafe ve o aralıkta açık olan segmentler. */
export interface Leg {
  hours: number;
  distanceNm: number;
  open: ReadonlySet<Segment>;
}

function legDistance(report: PerformanceReport): number {
  const source =
    report.kind === 'noon'
      ? (report.data.navigation as Record<string, unknown> | undefined)
      : report.data;
  const value = source?.distanceSinceLast;
  return typeof value === 'number' ? value : 0;
}

/**
 * `legs[i]`, `reports[i - 1]` → `reports[i]` aralığıdır; `legs[0]` boştur. Raporlar zamana göre
 * sıralı gelir. Paket içinde iyi hava günleri de kullanır (`good-weather.ts`).
 */
export function buildLegs(reports: readonly PerformanceReport[]): Leg[] {
  const legs: Leg[] = [];
  const open = new Set<Segment>();
  reports.forEach((report, index) => {
    const previous = reports[index - 1];
    legs.push({
      hours: previous ? hoursBetween(previous.reportedAt, report.reportedAt) : 0,
      distanceNm: previous ? legDistance(report) : 0,
      open: new Set(open),
    });
    const opens = OPENS[report.kind];
    const closes = CLOSES[report.kind];
    if (opens) open.add(opens);
    if (closes) open.delete(closes);
  });
  return legs;
}

/** COSP ve EOSP'nin ROB tablosu (`reportRob`); satır yoksa `null`. */
function robOf(report: PerformanceReport): Map<string, number> | null {
  const rob = reportRob(report);
  return rob.size > 0 ? rob : null;
}

/** Noon'da yazılan ikmalin toplamı (`reportBunkered`). */
function bunkeredOf(report: PerformanceReport): number {
  let total = 0;
  for (const value of reportBunkered(report).values()) total += value;
  return total;
}

/** Tek bir COSP → EOSP penceresi (EOSP yoksa seyir sürüyor; M6f G4-68). */
export interface CpWindow {
  start: string;
  end: string | null;
  windowHours: number;
  excludedHours: number;
  hours: number;
  distanceNm: number;
  avgSpeedKn: number | null;
}

export interface CpPerformance {
  start: string;
  /** Son EOSP; seyir sürüyorsa `null` ve hesap son rapora kadardır. */
  end: string | null;
  /** COSP → EOSP toplam süresi. */
  windowHours: number;
  /** Boğaz, kanal, demir, sürüklenme ve bekleme (kural 7, 10, 11). */
  excludedHours: number;
  hours: number;
  distanceNm: number;
  avgSpeedKn: number | null;
  /** COSP ve EOSP ROB farkı + seyirde ikmal; ROB eksikse `null`. */
  consumptionMt: number | null;
  /** Tüketim pencerenin tamamında olduğu için günlük değer pencere süresine bölünür (karar, ADR 0024 #16). */
  dailyConsumptionMt: number | null;
  loadCondition: LoadCondition | null;
  /** Pencereler ayrı ayrı; toplamları yukarıdaki değerlerdir. */
  windows: CpWindow[];
}

export interface PassageSegment {
  type: 'strait' | 'canal';
  name: string;
  start: string;
  end: string | null;
  hours: number;
  distanceNm: number;
  avgSpeedKn: number | null;
}

export interface OverallPerformance {
  start: string;
  end: string | null;
  hours: number;
  distanceNm: number;
  avgSpeedKn: number | null;
}

export interface VoyagePerformance {
  cp: CpPerformance | null;
  segments: PassageSegment[];
  overall: OverallPerformance | null;
}

function cpPerformance(
  reports: readonly PerformanceReport[],
  legs: readonly Leg[],
): CpPerformance | null {
  let first: PerformanceReport | null = null;
  let end: string | null = null;
  let windowHours = 0;
  let excludedHours = 0;
  let distanceNm = 0;
  let consumption: number | null = 0;
  const windows: CpWindow[] = [];
  for (let i = 0; i < reports.length; i++) {
    const cosp = reports[i] as PerformanceReport;
    if (cosp.kind !== 'cosp') continue;
    first ??= cosp;
    let j = i + 1;
    while (j < reports.length && reports[j]?.kind !== 'eosp' && reports[j]?.kind !== 'cosp') j++;
    const eosp = reports[j]?.kind === 'eosp' ? (reports[j] as PerformanceReport) : null;
    const last = eosp ? j : Math.min(j, reports.length) - 1;
    let bunkered = 0;
    const own = { windowHours: 0, excludedHours: 0, distanceNm: 0 };
    for (let k = i + 1; k <= last; k++) {
      const leg = legs[k] as Leg;
      own.windowHours += leg.hours;
      if (leg.open.size > 0) own.excludedHours += leg.hours;
      else own.distanceNm += leg.distanceNm;
      bunkered += bunkeredOf(reports[k] as PerformanceReport);
    }
    windowHours += own.windowHours;
    excludedHours += own.excludedHours;
    distanceNm += own.distanceNm;
    const ownHours = own.windowHours - own.excludedHours;
    windows.push({
      start: cosp.reportedAt,
      end: eosp ? eosp.reportedAt : null,
      windowHours: round(own.windowHours),
      excludedHours: round(own.excludedHours),
      hours: round(ownHours),
      distanceNm: round(own.distanceNm),
      avgSpeedKn: averageSpeed(own.distanceNm, ownHours),
    });
    end = eosp ? eosp.reportedAt : null;
    const before = robOf(cosp);
    const after = eosp ? robOf(eosp) : null;
    if (consumption === null || !before || !after) {
      consumption = null;
    } else {
      for (const [fuel, rob] of before) {
        const closing = after.get(fuel);
        if (closing !== undefined) consumption += rob - closing;
      }
      consumption += bunkered;
    }
    i = eosp ? j : last;
  }
  if (!first) return null;
  const hours = windowHours - excludedHours;
  const load = first.data.loadCondition;
  return {
    start: first.reportedAt,
    end,
    windowHours: round(windowHours),
    excludedHours: round(excludedHours),
    hours: round(hours),
    distanceNm: round(distanceNm),
    avgSpeedKn: averageSpeed(distanceNm, hours),
    consumptionMt: consumption === null ? null : round(consumption),
    dailyConsumptionMt:
      consumption === null || windowHours <= 0 ? null : round((consumption / windowHours) * 24),
    loadCondition: load === 'laden' || load === 'ballast' ? load : null,
    windows,
  };
}

function passageSegments(
  reports: readonly PerformanceReport[],
  legs: readonly Leg[],
): PassageSegment[] {
  const segments: PassageSegment[] = [];
  reports.forEach((entry, i) => {
    const type =
      entry.kind === 'strait_entry' ? 'strait' : entry.kind === 'canal_entry' ? 'canal' : null;
    if (!type) return;
    const exitKind = `${type}_exit`;
    let j = i + 1;
    while (j < reports.length && reports[j]?.kind !== exitKind) j++;
    const exit = reports[j] ?? null;
    const last = exit ? j : reports.length - 1;
    let distanceNm = 0;
    for (let k = i + 1; k <= last; k++) distanceNm += (legs[k] as Leg).distanceNm;
    const hours = hoursBetween(entry.reportedAt, (reports[last] as PerformanceReport).reportedAt);
    segments.push({
      type,
      name: String(entry.data.name ?? ''),
      start: entry.reportedAt,
      end: exit ? exit.reportedAt : null,
      hours: round(hours),
      distanceNm: round(distanceNm),
      avgSpeedKn: averageSpeed(distanceNm, hours),
    });
  });
  return segments;
}

function overallPerformance(
  reports: readonly PerformanceReport[],
  legs: readonly Leg[],
  end: string | null,
): OverallPerformance | null {
  // Departure yoksa sefer ortasından başlamıştır: başlangıç ilk rapor (ADR 0047 §2). İlk
  // Departure'dan önce limanda biten önek varsa da öyle (ara liman; ADR 0058 §4)
  if (reports.length === 0) return null;
  const firstDeparture = reports.findIndex((r) => r.kind === 'departure');
  const startIndex =
    firstDeparture > 0 && reports.slice(0, firstDeparture).some((r) => r.kind === 'arrival')
      ? 0
      : Math.max(0, firstDeparture);
  const start = (reports[startIndex] as PerformanceReport).reportedAt;
  let distanceNm = 0;
  let lastAt = start;
  for (let k = startIndex + 1; k < reports.length; k++) {
    const report = reports[k] as PerformanceReport;
    if (end && Date.parse(report.reportedAt) > Date.parse(end)) break;
    distanceNm += (legs[k] as Leg).distanceNm;
    lastAt = report.reportedAt;
  }
  const hours = hoursBetween(start, end ?? lastAt);
  return {
    start,
    end,
    hours: round(hours),
    distanceNm: round(distanceNm),
    avgSpeedKn: averageSpeed(distanceNm, hours),
  };
}

/**
 * Seferin performansı (kural 4–15). CP: COSP → EOSP; boğaz, kanal, demir, sürüklenme ve bekleme
 * aralıkları süreden ve mesafeden çıkar. Boğaz ve kanal ayrı segmenttir. Overall: sefer başlangıcı
 * (Departure; yoksa ilk rapor, ADR 0047 §2) → bitişi, hepsi dahil. CP penceresi COSP ister:
 * ortasından başlayan seferde ilk COSP'a kadar CP hesabı yoktur. Mesafe raporlardaki "son rapordan bu yana" mesafelerin toplamıdır.
 * @param options.end Seferin bitişi (`voyagePeriods`); sürüyorsa `null`, hesap son rapora kadar.
 */
export function voyagePerformance(
  reports: readonly PerformanceReport[],
  options: { end: string | null },
): VoyagePerformance {
  const sorted = [...reports].sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt));
  const legs = buildLegs(sorted);
  return {
    cp: cpPerformance(sorted, legs),
    segments: passageSegments(sorted, legs),
    overall: overallPerformance(sorted, legs, options.end),
  };
}

export interface CpTerms {
  cpSpeedLadenKn?: number | null;
  cpSpeedBallastKn?: number | null;
  cpConsumptionLadenMt?: number | null;
  cpConsumptionBallastMt?: number | null;
}

export interface CpComparison {
  plannedSpeedKn: number | null;
  actualSpeedKn: number | null;
  speedDiffKn: number | null;
  plannedConsumptionMt: number | null;
  actualConsumptionMt: number | null;
  consumptionDiffMt: number | null;
  /** Hız CP'nin altında değil ve tüketim üstünde değil; tolerans uygulanmaz (en sıkı okuma). */
  withinLimits: boolean | null;
}

/** CP değerleriyle karşılaştırma; yük durumu (laden/ballast) bilinmiyorsa `null`. */
export function cpComparison(
  actual: {
    avgSpeedKn: number | null;
    dailyConsumptionMt: number | null;
    loadCondition: LoadCondition | null;
  },
  terms: CpTerms,
): CpComparison | null {
  if (!actual.loadCondition) return null;
  const laden = actual.loadCondition === 'laden';
  const plannedSpeedKn = (laden ? terms.cpSpeedLadenKn : terms.cpSpeedBallastKn) ?? null;
  const plannedConsumptionMt =
    (laden ? terms.cpConsumptionLadenMt : terms.cpConsumptionBallastMt) ?? null;
  const speedDiffKn =
    plannedSpeedKn !== null && actual.avgSpeedKn !== null
      ? round(actual.avgSpeedKn - plannedSpeedKn)
      : null;
  const consumptionDiffMt =
    plannedConsumptionMt !== null && actual.dailyConsumptionMt !== null
      ? round(actual.dailyConsumptionMt - plannedConsumptionMt)
      : null;
  const checks = [
    speedDiffKn === null ? null : speedDiffKn >= 0,
    consumptionDiffMt === null ? null : consumptionDiffMt <= 0,
  ].filter((c): c is boolean => c !== null);
  return {
    plannedSpeedKn,
    actualSpeedKn: actual.avgSpeedKn,
    speedDiffKn,
    plannedConsumptionMt,
    actualConsumptionMt: actual.dailyConsumptionMt,
    consumptionDiffMt,
    withinLimits: checks.length === 0 ? null : checks.every(Boolean),
  };
}

export interface CpVerdict {
  /**
   * `within`: hız ve tüketim biliniyor, ikisi de sınırda; `outside`: bilinen bir değer sınır dışı;
   * `partial`: bilinen değer sınırda ama öbürü bilinmiyor; `unknown`: hiçbiri bilinmiyor.
   */
  status: 'within' | 'outside' | 'partial' | 'unknown';
  speed: boolean | null;
  consumption: boolean | null;
}

/**
 * CP durumu (KARAR #96, M6f G4-81): tüketim ya da hız bilinmiyorsa sefer "CP içinde" sayılmaz,
 * kısmi gösterilir ("Speed within CP · consumption n/a"). En temkinli okuma: bilinmeyen değer uygun
 * varsayılmaz. `cpComparison.withinLimits` yalnız bilinenlerle hesaplar; ekranlar bunu kullanır.
 */
export function cpVerdict(comparison: CpComparison | null): CpVerdict {
  const speedDiff = comparison?.speedDiffKn ?? null;
  const consumptionDiff = comparison?.consumptionDiffMt ?? null;
  const speed = speedDiff === null ? null : speedDiff >= 0;
  const consumption = consumptionDiff === null ? null : consumptionDiff <= 0;
  const status =
    speed === false || consumption === false
      ? 'outside'
      : speed === null && consumption === null
        ? 'unknown'
        : speed === null || consumption === null
          ? 'partial'
          : 'within';
  return { status, speed, consumption };
}
